'use client'

import { BasicFormValues } from '@app/registration/_components/basic/initialFormValues'
import { useEarlyStepsEventTracking } from '@app/registration/_hooks/useEarlyStepsEventTracking'
import { preferredAvatarSizeFormat } from '@components/MemberAvatar/preferredAvatarSizeFormat'
import StepHeader from '@components/Registration/StepHeader'
import { Avatar, Box, Flag, IDPictogram, TextField, TextFieldProps, Typography } from '@internations/ds'
import { getPlaceholderProfileImageUrl } from '@routes/utils/getPlaceholderProfileImageUrl'
import { TRACKING_TYPE_REGISTRATION_STARTED } from '@tracking/events'
import { Field, FieldInputProps, useField, useFormikContext } from '@util/Formik'
import { formatImageUrl } from '@util/assets'
import createPasswordLabel from '@util/labels/createPassword'
import createYourAccountLabel from '@util/labels/createYourAccount'
import emailLabel from '@util/labels/email'
import firstNameLabel from '@util/labels/firstName'
import lastNameLabel from '@util/labels/lastName'
import profileBornInLabel from '@util/labels/profileBornIn'
import profileLivingInLabel from '@util/labels/profileLivingIn'
import repeatPasswordLabel from '@util/labels/repeatPassword'
import youAreInvitedByLabel from '@util/labels/youAreInvitedBy'
import { BasicRegistrationFormErrors, maxLengthName } from '@util/registration/BasicFormModel'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { PasswordVisibilityAdornment } from '@components/PasswordVisibilityAdornment'

const TextFieldWithProps = (props: FieldInputProps<keyof BasicFormValues>) => <TextField fullWidth {...props} />

type TextInputProps = Omit<TextFieldProps, 'name' | 'data-testid'> & {
    name: keyof BasicFormValues
    'data-testid'?: string
    readOnly?: boolean
    maxLength?: number
}

export const TextInput = ({ name, label, type, maxLength, ...props }: TextInputProps): ReactElement => {
    const [field, meta] = useField<string>(name)
    const [isFocused, setIsFocused] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    const hasError = Boolean(meta.error && meta.touched)
    const isPasswordField = type === 'password'
    const inputType = isPasswordField && showPassword ? 'text' : type

    return (
        <Field
            type={inputType ? inputType : 'text'}
            name={name}
            label={label}
            as={TextFieldWithProps}
            error={hasError}
            helperText={hasError ? meta.error : null}
            InputLabelProps={{ shrink: isFocused || Boolean(field.value) }}
            inputProps={{
                ...props,
                maxLength,
            }}
            InputProps={{
                endAdornment: isPasswordField && (
                    <PasswordVisibilityAdornment showPassword={showPassword} setShowPassword={setShowPassword} />
                ),
            }}
            onFocus={() => {
                setIsFocused(true)
            }}
        />
    )
}

type InviterCardProps = {
    inviter: TMember
}

function InviterCard({ inviter }: InviterCardProps): ReactElement {
    const size = 'large'
    const origin = inviter.origin.country
    const residency = inviter.residency.country

    return (
        <Box margin="auto" data-testid="inviterCard">
            <Typography variant="caption" color="text.secondary">
                {youAreInvitedByLabel}
            </Typography>
            <Box display="flex" flexDirection="row" alignItems="center" mt={1}>
                <Avatar
                    size={size}
                    alt="image"
                    src={
                        inviter.imagePath
                            ? formatImageUrl(inviter.imagePath, size ? preferredAvatarSizeFormat[size] : '120_120')
                            : getPlaceholderProfileImageUrl(inviter.gender)
                    }
                />
                <Box display="flex" flexDirection="column" ml={1}>
                    <Typography variant="h3" color="primary.main">
                        {inviter.firstName} {inviter.lastName}
                    </Typography>
                    <Box>
                        <Box mr={1} display="inline">
                            <Flag code={origin.iocCode} title={origin.name} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="inline">
                            {profileBornInLabel(inviter)}
                        </Typography>
                    </Box>
                    <Box>
                        <Box mr={1} display="inline">
                            <Flag code={residency.iocCode} title={residency.name} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="inline">
                            {profileLivingInLabel(inviter)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

type FirstScreenProps = {
    inviter?: TMember
    isEmailReadOnly?: boolean
    backendErrors?: BasicRegistrationFormErrors
}

function FirstScreen({ inviter, backendErrors, isEmailReadOnly }: FirstScreenProps): ReactElement {
    const { mutateAsync: trackEvent } = useEarlyStepsEventTracking()
    const registrationStartedEventTrackedRef = useRef(false)
    const { setErrors } = useFormikContext()

    useEffect(() => {
        if (backendErrors) {
            setErrors(backendErrors)
        }
    }, [backendErrors, setErrors])

    const changeEmailNotice =
        'Once you complete your registration, you will be able to change your email address at any time.'

    return (
        <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center">
            <Box width="100%">
                <StepHeader Pictogram={IDPictogram} heading={createYourAccountLabel} />
                {inviter ? (
                    <Box mb={3} display="flex" alignItems="center">
                        <InviterCard inviter={inviter} />
                    </Box>
                ) : null}
                <Box mb={2} display={['block', 'flex']}>
                    <Box flex="0 0 50%" pr={[0, 2]} mb={[2, 0]}>
                        <TextInput
                            name="firstName"
                            label={firstNameLabel}
                            data-testid="firstName"
                            autoComplete="given-name"
                            maxLength={maxLengthName}
                        />
                    </Box>
                    <Box flex="0 0 50%">
                        <TextInput
                            name="lastName"
                            label={lastNameLabel}
                            data-testid="lastName"
                            autoComplete="family-name"
                            maxLength={maxLengthName}
                        />
                    </Box>
                </Box>

                <Box mb={2}>
                    <TextInput
                        name="email"
                        label={emailLabel}
                        type="email"
                        data-testid="email"
                        autoComplete="email"
                        readOnly={isEmailReadOnly}
                        disabled={isEmailReadOnly}
                    />
                    {inviter ? (
                        <Box pt={2} fontStyle="italic">
                            <Typography
                                variant="subtitle2"
                                color="textSecondary"
                                component="p"
                                data-testid="changeEmailNotice"
                            >
                                {changeEmailNotice}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>

                <Box mb={2}>
                    <TextInput
                        type="password"
                        name="password"
                        label={createPasswordLabel}
                        data-testid="password"
                        autoComplete="new-password"
                        onFocus={() => {
                            if (!registrationStartedEventTrackedRef.current) {
                                trackEvent(
                                    {
                                        eventName: TRACKING_TYPE_REGISTRATION_STARTED,
                                    },
                                    {
                                        onSuccess: () => {
                                            registrationStartedEventTrackedRef.current = true
                                        },
                                    }
                                )
                            }
                        }}
                    />
                </Box>

                <Box mb={2}>
                    <TextInput
                        type="password"
                        name="repeatPassword"
                        label={repeatPasswordLabel}
                        data-testid="repeatPassword"
                        autoComplete="new-password"
                    />
                </Box>
            </Box>
        </Box>
    )
}

export default FirstScreen
